import React, { useEffect, useMemo, useState } from "react";
import useAPI from "effects/useAPI";
import getDigibasicFeedRate from "services/digiBasic/getDigibasicFeedRate";
import LatestSubmissionsTable from "./components/LatestSubmissionsTable";
import {
  MATERIAL_SELECTION,
  MILLING_TYPE_SELECTION,
} from "./constants/constants";
import { buildHistoryExportRows } from "./utils/historyExport";
import { downloadCSV } from "./utils/csvDownloader";
import "./index.css";

function History() {
  const [feedRateToolId, setFeedRateToolId] = useState(null);
  const [selectedToolData, setSelectedToolData] = useState(null);
  const [selectedFeedRateData, setSelectedFeedRateData] = useState(null);

  const [
    loadingDigibasicFeedRate,
    errorDigibasicFeedRate,
    digibasicFeedRateResponse,
  ] = useAPI(() => {
    if (!feedRateToolId) return Promise.resolve(null);
    return getDigibasicFeedRate({ feedRateToolId });
  }, [feedRateToolId]);

  useEffect(() => {
    if (
      !loadingDigibasicFeedRate &&
      !errorDigibasicFeedRate &&
      digibasicFeedRateResponse?.digibasicFeedRate?.length > 0
    ) {
      setSelectedFeedRateData(digibasicFeedRateResponse.digibasicFeedRate[1]);
    } else {
      setSelectedFeedRateData(null);
    }
  }, [
    loadingDigibasicFeedRate,
    errorDigibasicFeedRate,
    digibasicFeedRateResponse,
  ]);

  const millingTypeLabel = useMemo(() => {
    return (
      MILLING_TYPE_SELECTION.find(
        (item) =>
          item.value === selectedToolData?.cuttingParameters?.millingTypeSelection
      )?.label || ""
    );
  }, [selectedToolData]);

  const materialLabel = useMemo(() => {
    return (
      MATERIAL_SELECTION.find(
        (item) =>
          item.value === selectedToolData?.cuttingParameters?.materialSelection
      )?.label || ""
    );
  }, [selectedToolData]);

  const handleExport = (seriesType = "original") => {
    if (!selectedToolData || !selectedFeedRateData) return;

    const rows = buildHistoryExportRows(
      selectedToolData,
      selectedFeedRateData,
      seriesType
    );

    const refNo = selectedToolData?.toolInputRefNo || "history";
    downloadCSV(rows, `${refNo}-${seriesType}-history-export.csv`);
  };

  return (
    <div className="history-container">
      <h1 className="history-page-title">History</h1>

      <LatestSubmissionsTable
        setFeedRateToolId={setFeedRateToolId}
        setSelectedToolData={setSelectedToolData}
        selectedToolId={feedRateToolId}
      />

      <div className="details-wrapper">
        <div className="details-card">
          <div className="details-header">
            <div>
              <h3 className="details-title">Details</h3>
              <p className="details-submission">
                Submission: {selectedToolData?.toolInputRefNo || "-"}
              </p>
            </div>

            <div className="details-actions">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleExport("original")}
                disabled={!selectedToolData || !selectedFeedRateData}
              >
                Export Original CSV
              </button>

              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={() => handleExport("optimized")}
                disabled={!selectedToolData || !selectedFeedRateData}
              >
                Export Optimized CSV
              </button>
            </div>
          </div>

          {!selectedToolData ? (
            <div className="empty-state">
              Select a row from the history table to view details.
            </div>
          ) : (
            <div className="details-grid">
              <div className="details-section">
                <h4>Input Parameters</h4>
                <table className="table table-bordered details-table">
                  <tbody>
                    <tr>
                      <th>Tool Type</th>
                      <td>{selectedToolData?.digibasicToolType || "-"}</td>
                    </tr>
                    <tr>
                      <th>Tool Diameter (mm)</th>
                      <td>{selectedToolData?.diameter ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Tool Number of Flutes</th>
                      <td>
                        {selectedToolData?.fluteParameters?.helixAngle?.length ??
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Tool Helix Angle (deg)</th>
                      <td>
                        {selectedToolData?.fluteParameters?.helixAngle?.join(
                          ", "
                        ) || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Tool Pitch Angle (deg)</th>
                      <td>
                        {selectedToolData?.fluteParameters?.pitchAngle?.join(
                          ", "
                        ) || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Depth of Cut (mm)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.depthOfCut ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Width of Cut (mm)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.widthOfCut ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Milling Type</th>
                      <td>{millingTypeLabel || "-"}</td>
                    </tr>
                    <tr>
                      <th>Spindle Speed (RPM)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.spindleSpeed ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Cutting Speed (m/min)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.cuttingSpeed ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Feed Rate (mm/min)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.feedRate ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Feed per Tooth (mm/tooth)</th>
                      <td>
                        {selectedToolData?.cuttingParameters?.feedPerTooth ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Material Selection</th>
                      <td>{materialLabel || "-"}</td>
                    </tr>
                    <tr>
                      <th>Rake Angle</th>
                      <td>{selectedToolData?.fluteParameters?.rakeAngle ?? "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="details-section">
                <h4>Results</h4>
                <table className="table table-bordered details-table">
                  <tbody>
                    <tr>
                      <th>Max Feed Force (N)</th>
                      <td>
                        {selectedFeedRateData?.original?.Max_Feed_Force ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Max Cross Feed Force (N)</th>
                      <td>
                        {selectedFeedRateData?.original?.Max_CrossFeed_Force ??
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Max Axial Force (N)</th>
                      <td>
                        {selectedFeedRateData?.original?.Max_Axial_Force ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Max Side Force (N)</th>
                      <td>
                        {selectedFeedRateData?.original?.Max_Side_Force ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Max Torque (Nm)</th>
                      <td>{selectedFeedRateData?.original?.Max_Torque ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Max Power (W)</th>
                      <td>{selectedFeedRateData?.original?.Max_Power ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Max Chip Thickness (mm)</th>
                      <td>
                        {selectedFeedRateData?.original?.Max_ChipThickness ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Material Removal Rate (MRR)</th>
                      <td>{selectedFeedRateData?.original?.MRR ?? "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="details-section">
                <h4>Feed Rate Optimisation</h4>
                <table className="table table-bordered details-table">
                  <tbody>
                    <tr>
                      <th>Optimisation Constraint</th>
                      <td>
                        {selectedToolData?.digibasicOptimisationConstraint ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Constraint Magnitude</th>
                      <td>
                        {selectedToolData?.selectedConstraintMagnitude ?? "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Optimised Feed Rate (mm/min)</th>
                      <td>
                        {selectedFeedRateData?.optimum?.Feedrate_New ?? "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default History;